import React from "react";
import {
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Button,
  FormGroup,
  Label,
  Input,
} from "reactstrap";
import { Home, Monitor } from "react-feather";

/**
 * CreateDeviceModal - Modal for creating a new device in a room.
 * Props:
 *   isOpen: bool
 *   room: the room to create device in (creatingDeviceForRoom)
 *   formData: { name, description, deviceTypeId }
 *   onFormDataChange: (updatedFormData) => void
 *   onConfirm: () => void
 *   onCancel: () => void
 *   loading: bool (creatingDevice)
 *   deviceTypes: array
 *   loadingDeviceTypes: bool
 */
const CreateDeviceModal = ({
  isOpen,
  room,
  formData,
  onFormDataChange,
  onConfirm,
  onCancel,
  loading,
  deviceTypes,
  loadingDeviceTypes,
}) => {
  return (
    <Modal isOpen={isOpen} toggle={onCancel} size="md">
      <ModalHeader toggle={onCancel}>Create Device</ModalHeader>
      <ModalBody>
        <div className="mb-3 p-3 bg-light rounded">
          <div className="d-flex align-items-center">
            <Home size={20} className="me-2 text-primary" />
            <div>
              <small className="text-muted">Creating device in room:</small>
              <div className="fw-bold">{room?.name}</div>
            </div>
          </div>
        </div>

        <FormGroup>
          <Label for="deviceName">
            Device Name <span className="text-danger">*</span>
          </Label>
          <Input
            id="deviceName"
            type="text"
            placeholder="Enter device name"
            value={formData.name}
            onChange={(e) => onFormDataChange({ ...formData, name: e.target.value })}
            disabled={loading}
          />
        </FormGroup>

        <FormGroup>
          <Label for="deviceDescription">Description</Label>
          <Input
            id="deviceDescription"
            type="textarea"
            placeholder="Enter device description"
            value={formData.description}
            onChange={(e) => onFormDataChange({ ...formData, description: e.target.value })}
            disabled={loading}
            rows="3"
          />
        </FormGroup>

        <FormGroup>
          <Label for="deviceType">
            Device Type <span className="text-danger">*</span>
          </Label>
          <Input
            id="deviceType"
            type="select"
            value={formData.deviceTypeId}
            onChange={(e) => onFormDataChange({ ...formData, deviceTypeId: e.target.value })}
            disabled={loading || loadingDeviceTypes}
          >
            <option value="">Select device type...</option>
            {deviceTypes.map((type) => (
              <option key={type.id} value={type.id}>
                {type.name} - {type.description || "No description"}
              </option>
            ))}
          </Input>
          {loadingDeviceTypes && <small className="text-muted">Loading device types...</small>}
        </FormGroup>

        <div className="alert alert-info" style={{ fontSize: "0.85rem", padding: "8px 12px" }}>
          <strong>Note:</strong> The device will be created in room "{room?.name}" (ID: {room?.id})
        </div>
      </ModalBody>
      <ModalFooter>
        <Button color="secondary" onClick={onCancel} disabled={loading}>
          Cancel
        </Button>
        <Button
          color="success"
          onClick={onConfirm}
          disabled={loading || loadingDeviceTypes}
        >
          {loading ? (
            <>
              <span className="spinner-border spinner-border-sm me-2"></span>
              Creating...
            </>
          ) : (
            <>
              <Monitor size={14} className="me-1" />
              Create Device
            </>
          )}
        </Button>
      </ModalFooter>
    </Modal>
  );
};

export default CreateDeviceModal;
